import axios from 'axios';

// Create axios instance with base URL
// In development, the proxy in package.json handles /api requests
// In production, nginx proxies /api to the backend
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Custom event for server unavailable
export const SERVER_UNAVAILABLE_EVENT = 'remidy:serverUnavailable';

// Helper to dispatch server unavailable event
const dispatchServerUnavailable = () => {
  window.dispatchEvent(new CustomEvent(SERVER_UNAVAILABLE_EVENT));
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check for network/server errors (no response received)
    // Skip this check for health endpoint to avoid infinite loops
    const isHealthCheck = error.config?.url?.includes('/health');
    const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
    const isGatewayError = error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504;
    
    // Don't treat maintenance mode 503 as server unavailable
    const isMaintenanceMode = error.response?.status === 503 && error.response?.data?.maintenanceMode;
    
    if (!isHealthCheck && (isNetworkError || (isGatewayError && !isMaintenanceMode))) {
      // Server is unavailable - dispatch global event
      console.log('[API] Server unavailable detected, dispatching event');
      dispatchServerUnavailable();
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      // Only redirect if user was previously authenticated (has a token)
      // This prevents redirect loops during setup wizard
      const hadToken = localStorage.getItem('auth_token');
      
      // Clear auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('household');
      
      // Only redirect if user was logged in (had a token)
      if (hadToken) {
        window.location.href = '/login';
      }
    }
    
    // Handle maintenance mode (503 with maintenanceMode flag)
    if (error.response?.status === 503 && error.response?.data?.maintenanceMode) {
      // Store maintenance message
      localStorage.setItem('maintenance_message', error.response.data.message);
      // Clear auth and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('household');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Setup API (for initial setup wizard)
export const setupAPI = {
  getStatus: () => api.get('/setup/status'),
  complete: (data) => api.post('/setup/complete', data)
};

// Health API (for startup readiness check)
export const healthAPI = {
  checkReady: () => api.get('/health/ready')
};

// Invite API (for email invitations)
export const inviteAPI = {
  validate: (token) => api.get(`/invite/validate/${token}`),
  accept: (token, password) => api.post('/invite/accept', { token, password })
};

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  updatePreferences: (preferences) => api.patch('/auth/preferences', { preferences }),
  updateProfile: (data) => api.patch('/auth/profile', data),
  updatePassword: (data) => api.post('/auth/password', data),
  getPasswordRequirements: () => api.get('/settings/authentication'),
  getAvailableUsers: () => api.get('/auth/available-users'),
  deleteAccount: (password, confirmation) => api.post('/auth/delete-account', { password, confirmation })
};

// Household API
export const householdAPI = {
  getCurrent: () => api.get('/households/me'),
  getMembers: () => api.get('/households/members'),
  create: (data) => api.post('/households', data),
  update: (data) => api.patch('/households/me', data),
  delete: () => api.delete('/households/me'),
  getPublic: () => api.get('/households/public'),
  sendJoinRequest: (householdId) => api.post(`/households/${householdId}/join-request`),
  getJoinRequests: () => api.get('/households/join-requests'),
  getJoinRequestsCount: () => api.get('/households/join-requests/count'),
  processJoinRequest: (requestId, action) => api.patch(`/households/join-requests/${requestId}`, { action }),
  // Member management
  inviteUser: (userId, role) => api.post('/households/invite-user', { userId, role }),
  inviteByEmail: (data) => api.post('/households/invite-by-email', data),
  getMyInvitations: () => api.get('/households/my-invitations'),
  processInvitation: (invitationId, action) => api.patch(`/households/invitations/${invitationId}`, { action }),
  addMember: (userId, role) => api.post('/households/members', { userId, role }), // Deprecated
  updateMemberRole: (memberId, role) => api.patch(`/households/members/${memberId}/role`, { role }),
  toggleMemberStatus: (memberId, isActive) => api.patch(`/households/members/${memberId}/status`, { isActive }),
  removeMember: (memberId) => api.delete(`/households/members/${memberId}`)
};

// Payment Accounts API
export const paymentAccountAPI = {
  getAll: (includeInactive = false) => api.get('/payment-accounts', { params: { includeInactive } }),
  getById: (id) => api.get(`/payment-accounts/${id}`),
  create: (data) => api.post('/payment-accounts', data),
  update: (id, data) => api.patch(`/payment-accounts/${id}`, data),
  toggleActive: (id, isActive) => api.patch(`/payment-accounts/${id}/toggle`, { isActive }),
  delete: (id) => api.delete(`/payment-accounts/${id}`)
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  // Data Export
  getExportSummary: () => api.get('/users/me/export/summary'),
  exportData: () => api.get('/users/me/export')
};

// Admin API
export const adminAPI = {
  getInfo: () => api.get('/admin/info'),
  getAllUsers: () => api.get('/users'), // This will get all users for admins
  createUser: (data) => api.post('/users', data),
  updateUser: (userId, data) => api.patch(`/users/${userId}`, data),
  promoteToAdmin: (userId) => api.patch(`/users/${userId}`, { role: 'admin' }),
  toggleUserStatus: (userId, isActive) => api.patch(`/users/${userId}`, { is_active: isActive }),
  resetPassword: (userId, newPassword) => api.post(`/users/${userId}/reset-password`, { newPassword }),
  resendInvite: (userId) => api.post(`/users/${userId}/resend-invite`),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  // Households
  getAllHouseholds: () => api.get('/admin/households'),
  getHouseholdMembers: (householdId) => api.get(`/admin/households/${householdId}/members`),
  createHousehold: (data) => api.post('/admin/households', data),
  updateHousehold: (householdId, data) => api.patch(`/admin/households/${householdId}`, data),
  deleteHousehold: (householdId) => api.delete(`/admin/households/${householdId}`),
  // Settings
  getSettings: (key) => api.get(`/settings/${key}`),
  updateSettings: (key, settings) => api.put(`/settings/${key}`, { setting_value: settings }),
  // SMTP
  testSmtpConnection: () => api.post('/settings/smtp/test-connection'),
  sendTestEmail: (email) => api.post('/settings/smtp/test-email', { email }),
  // Logo API
  testLogoApi: () => api.post('/settings/logo-api/test'),
  // Database
  getDatabaseStats: () => api.get('/database/stats'),
  createBackup: () => api.post('/database/backup')
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`)
};

// Payments API
export const paymentsAPI = {
  getAll: (params = {}) => api.get('/payments', { params }),
  getUpcoming: (days = 30) => api.get('/payments/upcoming', { params: { days } }),
  getStats: (year, month) => api.get('/payments/stats', { params: { year, month } }),
  getTrend: (period = '1m') => api.get('/payments/trend', { params: { period } }),
  getById: (id) => api.get(`/payments/${id}`),
  record: (data) => api.post('/payments', data),
  update: (id, data) => api.patch(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`)
};

// Bill Categories API
export const billCategoriesAPI = {
  getAll: (includeInactive = false) => api.get('/bill-categories', { params: { includeInactive } }),
  getById: (id) => api.get(`/bill-categories/${id}`),
  getUsage: () => api.get('/bill-categories/usage'),
  create: (data) => api.post('/bill-categories', data),
  update: (id, data) => api.patch(`/bill-categories/${id}`, data),
  toggleActive: (id, isActive) => api.patch(`/bill-categories/${id}/toggle`, { isActive }),
  delete: (id) => api.delete(`/bill-categories/${id}`)
};

// Bills API
export const billsAPI = {
  getAll: (includeInactive = false) => api.get('/bills', { params: { includeInactive } }),
  getById: (id) => api.get(`/bills/${id}`),
  create: (data) => api.post('/bills', data),
  update: (id, data) => api.patch(`/bills/${id}`, data),
  toggleActive: (id, isActive) => api.patch(`/bills/${id}/toggle`, { isActive }),
  delete: (id) => api.delete(`/bills/${id}`),
  getByCategory: (category) => api.get(`/bills/category/${category}`),
  getByAssignedUser: (userId) => api.get(`/bills/assigned/${userId}`)
};

// MFA API
export const mfaAPI = {
  getStatus: () => api.get('/mfa/status'),
  setup: () => api.post('/mfa/setup'),
  enable: (token) => api.post('/mfa/enable', { token }),
  disable: (password) => api.post('/mfa/disable', { password }),
  verify: (token) => api.post('/mfa/verify', { token }),
  regenerateBackupCodes: (password) => api.post('/mfa/backup-codes/regenerate', { password }),
  adminReset: (userId) => api.post(`/mfa/admin/reset/${userId}`)
};

// Sessions API
export const sessionsAPI = {
  getAll: () => api.get('/sessions'),
  revoke: (sessionId) => api.delete(`/sessions/${sessionId}`),
  revokeOthers: () => api.post('/sessions/revoke-others'),
  revokeAll: () => api.post('/sessions/revoke-all')
};

// OAuth API
export const oauthAPI = {
  getProviders: () => api.get('/oauth/providers'),
  getAccounts: () => api.get('/oauth/accounts'),
  getAuthUrl: (provider, linkAccount = false) => 
    api.get(`/oauth/${provider}/authorize`, { params: { linkAccount } }),
  linkAccount: (provider, code, state) => 
    api.post(`/oauth/${provider}/link`, { code, state }),
  unlinkAccount: (provider) => api.delete(`/oauth/${provider}/unlink`)
};

// Logo API
export const logoAPI = {
  search: (query, limit = 5) => api.get('/proxy/logo/search', { params: { q: query, limit } }),
  getByDomain: (domain) => api.get('/proxy/logo', { params: { domain } }),
  getByName: (name, domain) => api.get('/proxy/logo', { params: { name, domain } })
};

// Income API
export const incomeAPI = {
  getAll: (includeHousehold = false) => api.get('/income', { params: { household: includeHousehold } }),
  getSummary: () => api.get('/income/summary'),
  getHouseholdSummary: () => api.get('/income/household'),
  getById: (id) => api.get(`/income/${id}`),
  create: (data) => api.post('/income', data),
  update: (id, data) => api.patch(`/income/${id}`, data),
  toggleActive: (id, isActive) => api.patch(`/income/${id}/toggle`, { isActive }),
  delete: (id) => api.delete(`/income/${id}`)
};

export default api;
